import { PaymentVerificationResponse } from '../types/purchase';
import { billingAttribution } from './billingAttribution';
import { RazorpayMethodConfig, RazorpayResponse } from '../types/razorpay';
import { trackVerifiedPurchase } from '../utils/analytics';
import { isIOSApp } from '../utils/platformDetection';
import { getPlanBillingInterval } from '../utils/pricingPlans';
import { openRazorpay } from '../utils/razorpayUtils';
import { isStripeProvider, redirectToStripeCheckout } from '../utils/stripeUtils';

import { finishTransaction, purchase, restorePurchases } from './appleIAPBridge';
import type { IAPTransactionResult } from './appleIAPBridge';
import { getAppleProducts, verifyAndFulfill } from './appleIAPService';
import { SubscriptionResponse, subscriptionService, TopUpOrderResponse, UserSubscription } from './subscriptionService';

/** Disable UPI / netbanking / wallet for non-INR so Razorpay shows international card methods. */
const buildMethodConfig = (currency: string): RazorpayMethodConfig | undefined => (
  currency !== 'INR' ? { upi: false, netbanking: false, wallet: false, card: true } : undefined
);

type ErrorWithResponse = {
  message?: string;
  response?: { data?: { detail?: unknown; message?: unknown } };
};

const getAppleIAPErrorMessage = (error: unknown, fallback: string): string => {
  const typedError = error as ErrorWithResponse;
  const responseData = typedError.response?.data;
  const serverMessage = responseData?.message || responseData?.detail;

  if (typeof serverMessage === 'string' && serverMessage.trim()) return serverMessage;

  return typedError.message || fallback;
};

/**
 * Initiate a credit top-up purchase.
 * Routes to Apple IAP on iOS, Stripe/Razorpay on web.
 */
export const initiateTopUp = async (
  topupAmount: number,
  userEmail: string | undefined,
  onSuccess: (response: RazorpayResponse, msg: string) => Promise<void>,
  currency: string = 'USD',
) => {
  // Apple IAP flow for iOS app
  if (isIOSApp()) {
    throw new Error('Use initiateAppleTopUp for iOS credit purchases');
  }

  const order: TopUpOrderResponse = await subscriptionService.createTopUpOrder(topupAmount, currency);

  // Route to Stripe if backend indicates stripe provider
  if (isStripeProvider(order) && order.stripe_checkout_url) {
    redirectToStripeCheckout(order.stripe_checkout_url);

    return;
  }

  openRazorpay({
    key: order.key_id,
    amount: order.amount,
    currency: order.currency,
    name: 'MostlyAgent',
    description: `Top-up ${topupAmount} Credits`,
    order_id: order.order_id,
    handler: (response: RazorpayResponse) => onSuccess(response, 'Credits added successfully!'),
    prefill: { email: userEmail },
    method: buildMethodConfig(order.currency),
  });
};

/**
 * Initiate an Apple IAP credit top-up.
 *
 * @param productId - Apple product ID (e.g. 'com.app.ai8080.credits.USD10')
 * @param onSuccess - Called after backend confirms credits
 * @param onError - Called on failure
 * @param userId - User's UUID, passed as appAccountToken to Apple for webhook-based fulfillment
 */
export const initiateAppleTopUp = async (
  productId: string,
  onSuccess: (creditsGranted: number) => void,
  onError: (error: string) => void,
  userId?: string,
) => {
  const journeyId = await billingAttribution.startCheckout({ intent: 'credit_topup', product_id: productId });
  try {
    // 1. Purchase via StoreKit (with appAccountToken for webhook-based fulfillment)
    const result = await purchase(productId, userId);

    // 2. Verify and fulfill with backend (primary fulfillment path)
    const verification = await verifyAndFulfill(result.jwsTransaction, 'credits');
    await billingAttribution.linkCheckout(journeyId, {
      payment_provider: 'apple',
      provider_reference: result.transactionId,
    });

    if (verification.status === 'error') {
      throw new Error(verification.message || 'Verification failed');
    }

    trackVerifiedPurchase(verification.purchase);

    // 3. Finish transaction after backend confirms
    finishTransaction(result.transactionId);

    onSuccess(verification.credits_granted || 0);
  } catch (error: unknown) {
    onError(getAppleIAPErrorMessage(error, 'Apple IAP purchase failed'));
  }
};

/**
 * Initiate a subscription upgrade.
 * Routes to Apple IAP on iOS, Stripe/Razorpay on web.
 */
export const initiateUpgrade = async (
  tier: string,
  isAnnual: boolean,
  userEmail: string | undefined,
  onSuccess: (response: RazorpayResponse, msg: string) => Promise<void>,
  currency: string,
) => {
  // Apple IAP flow for iOS app
  if (isIOSApp()) {
    throw new Error('Use initiateAppleSubscription for iOS subscription purchases');
  }

  const subData: SubscriptionResponse = await subscriptionService.createSubscription(
    tier,
    getPlanBillingInterval(tier, isAnnual),
    currency
  );

  // Route to Stripe if backend indicates stripe provider
  if (isStripeProvider(subData) && subData.stripe_checkout_url) {
    redirectToStripeCheckout(subData.stripe_checkout_url);

    return;
  }

  openRazorpay({
    key: subData.key_id,
    subscription_id: subData.subscription_id,
    name: 'MostlyAgent',
    description: `Upgrade to ${tier}`,
    handler: (response: RazorpayResponse) => onSuccess(response, `Successfully upgraded to ${tier}!`),
    prefill: { email: userEmail },
    method: buildMethodConfig(currency),
  });
};

/**
 * Initiate an Apple IAP subscription purchase.
 *
 * Handles the full lifecycle for a user-initiated subscription purchase:
 * 1. Attempt purchase via StoreKit
 * 2. Verify with backend
 * 3. Backend checks Apple Account binding (1 Apple ID = 1 8080 account)
 * 4. Returns success or specific error (APPLE_ACCOUNT_CONFLICT)
 *
 * @param productId - Apple subscription product ID
 * @param onSuccess - Called after backend confirms subscription
 * @param onError - Called on failure with user-facing error message
 * @param userId - User's UUID, passed as appAccountToken for webhook fallback
 */
export const initiateAppleSubscription = async (
  productId: string,
  onSuccess: (tier: string) => void,
  onError: (error: string) => void,
  userId?: string,
) => {
  const journeyId = await billingAttribution.startCheckout({ intent: 'upgrade', product_id: productId });
  try {
    // 1. Attempt purchase via StoreKit (with appAccountToken for webhook fallback)
    const result = await purchase(productId, userId);

    // 2. Verify and fulfill with backend (primary fulfillment path)
    const verification = await verifyAndFulfill(result.jwsTransaction, 'subscription');
    await billingAttribution.linkCheckout(journeyId, {
      payment_provider: 'apple',
      provider_reference: result.transactionId,
    });

    if (verification.status === 'error' && verification.error_code === 'APPLE_ACCOUNT_CONFLICT') {
      onError(verification.message || 'This subscription is already active on another account.');
      return;
    }

    if (verification.status === 'error') {
      throw new Error(verification.message || 'Verification failed');
    }

    trackVerifiedPurchase(verification.purchase);

    // 3. Finish transaction after backend confirms
    finishTransaction(result.transactionId);

    onSuccess(verification.tier || '');
  } catch (error: any) {
    // Keep restore as an explicit user action. Auto-restoring from the purchase
    // path can replay an old subscription transaction and make it look like a
    // plan unlocked without the normal App Store purchase flow.
    if (error.code === 'ALREADY_OWNED') {
      onError(
        'Apple says this subscription is already owned. Tap Restore Plan Purchase to sync your active plan, '
        + 'or manage your subscription in iOS Settings.',
      );
      return;
    }

    // Handle user cancellation silently
    if (error.message?.includes('cancelled') || error.message?.includes('canceled')) {
      return;
    }

    onError(getAppleIAPErrorMessage(error, 'Apple IAP subscription failed'));
  }
};

export interface AppleRestoreResult {
  status: 'restored' | 'none';
  tier?: string;
  productId?: string;
}

const getRestorableSubscriptionTransactions = async (): Promise<IAPTransactionResult[]> => {
  const restored = await restorePurchases();

  if (!restored || restored.length === 0) return [];

  const appleProducts = await getAppleProducts();
  const subscriptionProductIds = new Set(
    appleProducts.subscription_products.map((product) => product.product_id)
  );

  return restored.filter(
    (transaction) => transaction.jwsTransaction && subscriptionProductIds.has(transaction.productId)
  );
};

const isAppleAccountConflictError = (error: unknown): boolean => (
  error instanceof Error && (
    error.message.includes('already active on another account')
    || error.message.includes('linked to another 8080.AI account')
  )
);

type RestoreVerification = Awaited<ReturnType<typeof verifyAndFulfill>>;

const assertRestoreVerificationSucceeded = (verification: RestoreVerification): void => {
  if (verification.status === 'error' && verification.error_code === 'APPLE_ACCOUNT_CONFLICT') {
    throw new Error(
      verification.message
        || 'This subscription is already active on another account. Please log in with that account or contact support.'
    );
  }

  if (verification.status === 'error') {
    throw new Error(verification.message || 'Your restored subscription could not be verified. Try restoring it again.');
  }
};

const buildRestoreResult = (
  verification: RestoreVerification,
  transaction: IAPTransactionResult,
): AppleRestoreResult => ({
  status: 'restored',
  tier: verification.tier,
  productId: verification.product_id || transaction.productId,
});

const verifyRestoredSubscription = async (
  transaction: IAPTransactionResult,
): Promise<AppleRestoreResult> => {
  const verification = await verifyAndFulfill(transaction.jwsTransaction, 'subscription', true);

  assertRestoreVerificationSucceeded(verification);
  finishTransaction(transaction.transactionId);

  return buildRestoreResult(verification, transaction);
};

const restoreFirstAvailableSubscription = async (
  transactions: IAPTransactionResult[],
): Promise<AppleRestoreResult> => {
  let lastError: unknown;

  if (transactions.length === 0) return { status: 'none' };

  for (const transaction of transactions) {
    try {
      return await verifyRestoredSubscription(transaction);
    } catch (error) {
      lastError = error;
      if (isAppleAccountConflictError(error)) throw error;
    }
  }

  if (lastError) throw lastError;

  return { status: 'none' };
};

const assertAppleRestoreAvailable = (): void => {
  if (!isIOSApp()) {
    throw new Error('Apple plan restore is only available in the iOS app.');
  }
};

/**
 * User-initiated Apple subscription restore.
 *
 * Restored StoreKit transactions are still verified by the backend, which
 * enforces the 1:1 Apple original transaction ↔ 8080.AI account binding.
 */
export const restoreApplePurchasesForCurrentAccount = async (): Promise<AppleRestoreResult> => {
  assertAppleRestoreAvailable();

  try {
    return await restoreFirstAvailableSubscription(await getRestorableSubscriptionTransactions());
  } catch (error) {
    throw new Error(getAppleIAPErrorMessage(error, 'Your Apple plan could not be restored. Check your connection and try again.'));
  }
};

export const verifyTransaction = async (response: RazorpayResponse): Promise<PaymentVerificationResponse> => {
  let verification: PaymentVerificationResponse;

  if (response.razorpay_subscription_id) {
    verification = await subscriptionService.verifySubscription({
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_subscription_id: response.razorpay_subscription_id,
      razorpay_signature: response.razorpay_signature,
    });
  } else {
    // Cast to required type for verifyPayment which needs order_id
    verification = await subscriptionService.verifyPayment({
      razorpay_order_id: response.razorpay_order_id!,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
    });
  }

  trackVerifiedPurchase(verification.purchase);
  await billingAttribution.linkVerifiedProviderReference(response.razorpay_payment_id);

  return verification;
};

export const refreshSubscription = async (setCurrentSub: (sub: UserSubscription | null) => void) => {
  const subData = await subscriptionService.getCurrentSubscription();

  setCurrentSub(subData);
};
